import { useEffect, useMemo, useRef } from 'react';
import clsx from 'clsx';
import { ReasoningPill } from '@/components/builder/ReasoningPill';
import { PlanChecklist } from '@/components/builder/PlanChecklist';
import { SummaryCard } from '@/components/builder/SummaryCard';
import { ToolResultRenderer } from '@/components/builder/tool-results';
import { ArcanumMarkdown } from '@/components/ui/ArcanumMarkdown';
import { posthog } from '@/lib/posthog';
import { deriveToolCallStates } from '@/hooks/use-conversations-v2';
import { usePublicDealConversation } from '@/hooks/use-public-deal';
import { authUrlWithShare } from './auth-share-url';
import type {
  ShareConversationTurn,
  SuggestionChips,
  SummarySection,
  ToolName,
  PlanStep,
} from '@arcanum-hq/shared-types';
import type {
  StreamingMessage,
  ThinkingBlock,
  PlanState,
  ToolCallState,
} from '@/hooks/use-streaming-v2';
import type { NarrativeBarShareMode } from '@/components/layout/NarrativeBar';

/**
 * `deal-signal-overhaul` Phase 5 Session 3 (2026-05-13).
 *
 * User-visible chat surface on `/deal/:shareId`. Mounts BENEATH the
 * Signal body inside `<PublicChromeShell>`'s body slot. Hydrates from
 * `usePublicDealConversation` on mount + renders the in-flight stream
 * (passed via props from the wrapper that mints `useSharedStreaming`).
 *
 * Composition contract (Lovable-density inherited from Phase 4 Session
 * 6 — `apps/web/CLAUDE.md § "Signal page Lovable-aligned composition"`):
 *
 *   - Hairline `border-t border-panel-border` between Signal body +
 *     thread; NO `gap-6` separation.
 *   - Accent gradient anchor flipped vertically against the Phase 4
 *     hero strip (`linear-gradient(360deg, rgba(0,200,83,0.04) 0%,
 *     transparent 100%)`).
 *   - 10-11px metadata via `font-mono`; `text-xs` prose via `font-body`.
 *     NO `text-sm`/`text-base` chat bubbles, NO `ml-8` indent,
 *     NO `max-w-[65%]` clamp (those violate Phase 4 density).
 *   - ZoneHeader pattern (`accent-dot + uppercase label + flex-1
 *     hairline`) used for the "CONVERSATION" caption.
 *   - Reasoning + tool-call cards inside the thread use 1px border,
 *     `rounded-[2px]`, `px-2.5 py-2` — matches the `<PhaseCard>`
 *     compact-card pattern.
 *
 * Revisit-mode hydration: every persisted agent turn synthesizes
 * `toolCallStates` from `turn.tool_results` + `turn.tool_errors`
 * because Phase 3 Session 4 persists `tool_call_states: null` on
 * every turn (deliberate; reserved for a future extension). The
 * synthesizer mirrors `use-conversations-v2`'s auth-side helper —
 * one `legacy-${turnId}-${idx}` ID per result/error.
 *
 * Security-redaction rendering: NOT surfaced on the revisit path —
 * `ShareConversationTurn` does not carry a `security_*` field on the
 * wire (Phase 3 deliberately doesn't persist `security_redact` events).
 * Live stream surfaces redactions in-band via `useSharedStreaming`'s
 * `redactedSpans` accumulator; the streaming-turn renderer surfaces
 * them then.
 */

export interface PublicDealChatThreadProps {
  shareId: string;
  /**
   * Resolved share-mode from `routes/deal.tsx` `resolveShareMode`.
   * Currently surfaced for future per-mode chrome (e.g. "viewing as
   * recipient" caption); the thread body is mode-agnostic today.
   */
  shareMode?: NarrativeBarShareMode;
  /**
   * Suggestion chips from `usePublicDeal(shareId).data.suggestionChips`
   * (set on share creation by Phase 3 Session 3). When null, the
   * empty-state preamble renders without chip affordances.
   */
  suggestionChips?: SuggestionChips | null;
  /**
   * Live streaming state from the wrapper-owned `useSharedStreaming`
   * hook. Null when no stream is in flight. Threaded down so the thread
   * + composer share one hook instance (avoiding duplicate fetches).
   */
  currentTurn: StreamingMessage | null;
  /**
   * Optimistic echo of the visitor's just-submitted question (2026-06-04).
   * Rendered as an immediate right-aligned human bubble ABOVE the AI
   * streaming stub, co-gated on `showStreamingStub` so it appears in lockstep
   * with the stub and is atomically suppressed when the persisted human turn
   * lands via the post-`done` refetch (Phase 3 persists human Q + agent A in
   * ONE transaction → turns.length jumps +2 → stub replaced). Sourced from
   * `useSharedStreaming().pendingHuman`. Null when no stream is in flight.
   */
  pendingHuman?: string | null;
  /**
   * Submit a message. RESERVED + currently unused: the thread no longer hosts
   * any send affordance — the starter-question chips live on the NarrativeBar
   * (which owns sending via the wrapper's `sendGuarded`). Kept optional on the
   * prop contract for a potential future in-thread send affordance; the wrapper
   * still passes it. (2026-06-04 dedup.)
   */
  sendMessage?: (content: string, overrides?: Record<string, unknown>) => void;
}

/**
 * Tools whose `tool_result` cards are hidden in the receipt-grouping
 * iteration. Mirrors the auth-side `<ChatThread>` rule so plan
 * proposals + market-data enrichment + set_table_inputs don't render
 * as redundant receipt cards (they're either synthetic UI affordances
 * or narrated in prose).
 */
const HIDDEN_FROM_RECEIPT_GROUPING = new Set<ToolName>([
  'propose_plan',
  'market_data_enrichment',
  'set_table_inputs',
]);

// Narrowed reads off the `z.unknown().nullable().optional()` wire shape
// — the upstream Zod parse runs at write time inside `agents/streaming.ts`
// so these casts are safe (matches auth-side hydration pattern).
type WireToolResult = { tool: string; result: unknown };
type WireToolError = {
  tool: string;
  code: string;
  message: string;
  details?: Record<string, unknown>;
};

const noOpSynthesizedTurn = (_message: string): void => {
  // Public chat does NOT host `propose_table_fill` or other
  // turn-synthesizing tool affordances — the public agent's toolset is
  // narrower than the auth-side ReAct agent's. `<ToolResultRenderer>`
  // requires the prop unconditionally, so we wire a no-op.
};

function ZoneHeader() {
  // The free-question counter lives ONLY on the NarrativeBar header (the
  // always-present `narrative-bar-question-budget` chip). Rendering it here too
  // double-counted once the thread moved inside the bar's overlay — dropped
  // 2026-06-04. This header is now just the "CONVERSATION" label + hairline.
  return (
    <div
      data-testid="public-chat-zone-header"
      className="flex items-center gap-2.5 mb-2"
    >
      <span
        className="w-1 h-1 rounded-full bg-accent shrink-0"
        style={{ boxShadow: '0 0 4px rgba(0,200,83,0.6)' }}
      />
      <span className="text-[10px] font-mono uppercase tracking-[2px] text-accent font-bold">
        CONVERSATION
      </span>
      <span className="flex-1 h-px bg-panel-border" />
    </div>
  );
}

function DenseMessageBubble({
  role,
  content,
  turnId,
}: {
  role: 'human' | 'agent' | 'ai';
  content: string;
  turnId: string;
}) {
  const isHuman = role === 'human';
  // WS3 (public-deal-link-polish): the visitor's own question renders as a
  // right-aligned accent-tinted bubble — mirrors the internal Builder
  // MessageBubble user turn (justify-end + bg-accent-dim + border-accent/9)
  // MINUS the initials avatar (public viewers are anonymous). The AI turn
  // stays left-aligned/full-width so the transcript reads as a conversation.
  if (isHuman) {
    return (
      <div
        data-testid="dense-message-bubble"
        data-role={role}
        data-turn-id={turnId}
        className="flex justify-end py-1"
      >
        <div className="max-w-[75%] px-3 py-1.5 rounded-[4px] border bg-accent-dim border-accent/9 text-xs font-body text-text-bright whitespace-pre-wrap break-words">
          {content}
        </div>
      </div>
    );
  }
  // WS1: AI turn renders through the shared <ArcanumMarkdown> renderer at
  // compact density (text-xs) — tables/headings/lists/bold/code + arcanum
  // chips, replacing the prior raw whitespace-pre-wrap passthrough.
  return (
    <div
      data-testid="dense-message-bubble"
      data-role={role}
      data-turn-id={turnId}
      className="py-1"
    >
      <ArcanumMarkdown content={content} density="compact" />
    </div>
  );
}

function ReceiptCards({
  states,
  assistantContent,
  shareId,
}: {
  states: Map<string, ToolCallState>;
  /** Share id — threaded so the recovery-fallback Sign-up CTA carries it. */
  shareId: string;
  /**
   * The agent's final prose response for this turn. When non-empty, we
   * treat the turn as RECOVERED — failed tool calls are filtered out so
   * anonymous viewers don't see the agent's internal trial-and-error
   * (e.g. `get_values(["LTC"])` → `Unknown named range` errors before it
   * landed on the correct `LoanToCost` named range). The owner / auth-side
   * `<ChatThread>` still surfaces every error verbatim because it's a
   * transparency signal for someone who understands the agent's process.
   *
   * Empty content + only-errored states → render a single generic
   * fallback line ("I couldn't find that.") with the existing UNLOCK
   * ARCANUM CTA carrying the recovery path. We never leak the failed
   * tool names to the public viewer.
   */
  assistantContent: string;
}) {
  const visible = Array.from(states.entries()).filter(
    ([, state]) => !HIDDEN_FROM_RECEIPT_GROUPING.has(state.tool)
  );
  if (visible.length === 0) return null;
  const agentRecovered = assistantContent.trim().length > 0;
  const renderable = agentRecovered
    ? visible.filter(([, state]) => state.phase !== 'errored')
    : visible;
  if (renderable.length === 0) {
    // Agent gave up — empty prose + only errored tool calls. Show a
    // generic fallback (does not name the failed tools).
    if (!agentRecovered) {
      return (
        <div
          data-testid="public-chat-recovery-fallback"
          className="bg-panel-bg border border-panel-border rounded-[2px] px-2.5 py-2 text-xs font-body text-text-muted"
        >
          I couldn&apos;t find that on this deal.{' '}
          <a
            href={authUrlWithShare('/signup', shareId)}
            onClick={() =>
              posthog.capture('deal_signup_cta_clicked', {
                source: 'recovery-fallback',
                share_id: shareId,
              })
            }
            className="text-accent hover:underline"
          >
            Sign up
          </a>{' '}
          for full access.
        </div>
      );
    }
    return null;
  }
  return (
    <div className="flex flex-col gap-1.5">
      {renderable.map(([id, state]) => (
        <div
          key={id}
          className="bg-panel-bg border border-panel-border rounded-[2px] px-2.5 py-2"
        >
          <ToolResultRenderer
            state={state}
            onSynthesizedTurn={noOpSynthesizedTurn}
          />
        </div>
      ))}
    </div>
  );
}

function DenseTurnRenderer({
  turn,
  shareId,
}: {
  turn: ShareConversationTurn;
  shareId: string;
}) {
  const isAgent = turn.role === 'agent';

  // Synthesize per-turn `toolCallStates` from the wire-shaped
  // `tool_results` + `tool_errors` arrays. Phase 3 persists
  // `tool_call_states: null`; the auth-side parity bridge does the
  // same synthesis at hydration time (use-conversations-v2:262).
  const toolCallStates = useMemo<Map<string, ToolCallState>>(() => {
    if (!isAgent) return new Map();
    const results =
      (turn.tool_results as WireToolResult[] | null | undefined) ?? undefined;
    const errors =
      (turn.tool_errors as WireToolError[] | null | undefined) ?? undefined;
    return deriveToolCallStates(turn.id, results, errors);
  }, [isAgent, turn.id, turn.tool_results, turn.tool_errors]);

  // `turn.plan` / `turn.thinking` / `turn.summary` are persisted as the
  // RAW SSE event payloads (sharing.service.ts aggregator at ~line 2400
  // assigns `aggregated.plan = sse` etc.). The client-side state shapes
  // (`PlanState` with `completedStepIds: Set<string>`, `ThinkingBlock` with
  // camelCase `startedAt` / `durationMs`) are derived at stream time and
  // never persisted. On revisit-mode hydration we synthesize the client
  // shapes back — `completedStepIds` collapses to ALL steps (turn finished
  // so we have no per-step granularity, defaulting to all-complete is the
  // safe display); snake_case → camelCase mapping for thinking. Crashes
  // surfaced 2026-05-12 — `PlanChecklist` called `.has()` on undefined.
  const plan = useMemo<PlanState | null>(() => {
    if (!isAgent) return null;
    const raw = turn.plan as { steps?: PlanStep[] } | null | undefined;
    if (!raw || !Array.isArray(raw.steps) || raw.steps.length === 0)
      return null;
    return {
      steps: raw.steps,
      activeStepId: null,
      completedStepIds: new Set(raw.steps.map((s) => s.step_id)),
      erroredStepId: null,
    };
  }, [isAgent, turn.plan]);

  const thinking = useMemo<ThinkingBlock | null>(() => {
    if (!isAgent) return null;
    const raw = turn.thinking as
      | { duration_ms?: number; token_count?: number; text?: string }
      | null
      | undefined;
    if (!raw) return null;
    return {
      startedAt: 0,
      durationMs: typeof raw.duration_ms === 'number' ? raw.duration_ms : null,
      tokenCount: typeof raw.token_count === 'number' ? raw.token_count : null,
      text: typeof raw.text === 'string' ? raw.text : '',
    };
  }, [isAgent, turn.thinking]);

  const summary =
    (turn.summary as { sections: SummarySection[] } | null | undefined) ?? null;

  return (
    <div
      data-testid="dense-turn-renderer"
      data-role={turn.role}
      data-turn-id={turn.id}
      className="flex flex-col gap-1.5 py-1.5"
    >
      {isAgent && thinking && (
        <ReasoningPill thinking={thinking} isStreaming={false} />
      )}
      {isAgent && plan && (
        <div className="max-w-full">
          <PlanChecklist plan={plan} />
        </div>
      )}
      {isAgent && toolCallStates.size > 0 && (
        <ReceiptCards
          states={toolCallStates}
          assistantContent={turn.content ?? ''}
          shareId={shareId}
        />
      )}
      {turn.content && (
        <DenseMessageBubble
          role={turn.role}
          content={turn.content}
          turnId={turn.id}
        />
      )}
      {isAgent && summary && <SummaryCard sections={summary.sections} />}
    </div>
  );
}

function DenseStreamingTurnRenderer({
  turn,
  shareId,
}: {
  turn: StreamingMessage;
  shareId: string;
}) {
  return (
    <div
      data-testid="dense-streaming-turn"
      data-role="agent"
      data-turn-id={turn.id}
      data-streaming={turn.isStreaming ? 'true' : 'false'}
      className="flex flex-col gap-1.5 py-1.5"
    >
      {turn.thinking && (
        <ReasoningPill
          thinking={turn.thinking}
          isStreaming={turn.isStreaming}
        />
      )}
      {turn.plan && (
        <div className="max-w-full">
          <PlanChecklist plan={turn.plan} />
        </div>
      )}
      {turn.toolCallStates.size > 0 && (
        <ReceiptCards
          states={turn.toolCallStates}
          assistantContent={turn.content ?? ''}
          shareId={shareId}
        />
      )}
      {turn.content && (
        <DenseMessageBubble
          role="agent"
          content={turn.content}
          turnId={turn.id}
        />
      )}
      {turn.summary && <SummaryCard sections={turn.summary.sections} />}
    </div>
  );
}

function DenseEmptyStatePreamble() {
  // Empty conversation hint. The starter-question chips + the free-question
  // counter live on the NarrativeBar (always-present `narrative-bar-suggestion-
  // chip` + `narrative-bar-question-budget`); rendering them here too doubled
  // both once the thread moved inside the bar's overlay — dropped 2026-06-04.
  // This is now just a one-line "get started" hint.
  return (
    <div
      data-testid="dense-empty-state-preamble"
      className="px-8 py-4 text-[11px] font-mono text-text-muted"
    >
      Ask a question below to get started — your conversation will appear here.
    </div>
  );
}

export function PublicDealChatThread({
  shareId,
  shareMode,
  suggestionChips,
  currentTurn,
  pendingHuman,
}: PublicDealChatThreadProps) {
  const conversation = usePublicDealConversation(shareId);
  const turns = conversation.data?.turns ?? [];
  const questionCount = conversation.data?.questionCount ?? 0;

  // Stub-replacement bookkeeping (blueprint Step 5 — Optimistic stub
  // retention). When `currentTurn` first arrives we capture the current
  // `turns.length` as a baseline. After the SSE `done` event fires the
  // hook invalidates `['public-deal-conversation', shareId]`, the
  // refetch lands, and `turns.length` jumps past the baseline (Phase 3
  // persists human Q + agent A in a single transaction → +2). At that
  // point the persisted `<DenseTurnRenderer>` takes over and we suppress
  // the lingering `<DenseStreamingTurnRenderer>` stub — otherwise the
  // agent's response would render twice (once as persisted, once as
  // the stale streaming stub) until the next `sendMessage` or `abort`
  // clears it. Baseline is keyed on `currentTurn.id` so a NEW
  // `sendMessage` (different stub id) re-captures the baseline against
  // the now-grown `turns.length`.
  const baselineRef = useRef<{ turnId: string; turnsCount: number } | null>(
    null
  );
  useEffect(() => {
    if (currentTurn == null) {
      baselineRef.current = null;
      return;
    }
    if (baselineRef.current?.turnId !== currentTurn.id) {
      baselineRef.current = {
        turnId: currentTurn.id,
        turnsCount: turns.length,
      };
    }
  }, [currentTurn?.id, turns.length]);

  const stubReplacedByPersisted =
    currentTurn != null &&
    baselineRef.current != null &&
    turns.length > baselineRef.current.turnsCount;
  const showStreamingStub = currentTurn != null && !stubReplacedByPersisted;

  const isEmpty = turns.length === 0 && !showStreamingStub;

  // `deal-signal-overhaul` Phase 5 redesign 2026-05-12 — in-thread gate
  // message. When the 3-question gate fires, the conversation hook
  // surfaces `gateActive: true`. Render a synthetic "From Arcanum"
  // entry at the bottom of the thread (alongside the persisted turns)
  // instead of swapping the composer body. Unified across anon +
  // external-authed viewers per user decision.
  const gateActive =
    (conversation.data?.gateActive ?? false) || questionCount >= 3;

  return (
    <section
      data-testid="public-deal-chat-thread"
      data-share-mode={shareMode ?? 'unknown'}
      data-empty={isEmpty ? 'true' : 'false'}
      data-gate-active={gateActive ? 'true' : 'false'}
      className="flex flex-col"
      style={{
        background:
          'linear-gradient(360deg, rgba(0, 200, 83, 0.04) 0%, transparent 100%)',
      }}
    >
      {!isEmpty && (
        <div className="flex flex-col px-8 py-3">
          <ZoneHeader />
          {turns.map((turn) => (
            <DenseTurnRenderer key={turn.id} turn={turn} shareId={shareId} />
          ))}
          {/* Optimistic human bubble — renders the visitor's question
              IMMEDIATELY, above the agent's streaming response. Co-gated on
              the same `showStreamingStub` flag as the AI stub so the pair
              appears and is suppressed atomically: the post-`done` refetch
              jumps turns.length +2 → `stubReplacedByPersisted` → both the
              optimistic human bubble AND the AI stub drop in the same render
              tick while the two persisted rows take over (no double bubble,
              no flicker). The `turnId` keys off currentTurn.id so a fresh
              submit re-keys cleanly. */}
          {showStreamingStub && pendingHuman && (
            <DenseMessageBubble
              role="human"
              content={pendingHuman}
              turnId={
                currentTurn
                  ? `pending-human-${currentTurn.id}`
                  : 'pending-human'
              }
            />
          )}
          {showStreamingStub && currentTurn && (
            <DenseStreamingTurnRenderer turn={currentTurn} shareId={shareId} />
          )}
          {gateActive && (
            <InThreadGateMessage
              shareMode={shareMode}
              shareId={shareId}
              suggestionChips={suggestionChips}
            />
          )}
        </div>
      )}
      {isEmpty && (
        <>
          <DenseEmptyStatePreamble />
          {gateActive && (
            <div className="px-8 pb-3">
              <InThreadGateMessage
                shareMode={shareMode}
                shareId={shareId}
                suggestionChips={suggestionChips}
              />
            </div>
          )}
        </>
      )}
    </section>
  );
}

function InThreadGateMessage({
  shareMode,
  shareId,
  suggestionChips,
}: {
  shareMode?: NarrativeBarShareMode;
  shareId: string;
  /**
   * The deal's 3 suggestion chips, rendered as greyed-out, NON-interactive
   * "what you could ask next" teasers beside the Unlock CTA (2026-06-04) —
   * showing the prospect the depth they're locked out of at the exact moment
   * they hit the gate. Display-only of the EXISTING array; NEVER add a 4th
   * slot or recompute (`suggestionChipsSchema` is `.length(3)`-locked).
   */
  suggestionChips?: SuggestionChips | null;
}) {
  return (
    <div
      data-testid="in-thread-gate-message"
      data-share-mode={shareMode}
      className="flex flex-col gap-2 my-2 px-3 py-2 border border-accent/20 rounded-[2px] bg-accent-dim"
    >
      <span className="text-[10px] font-mono uppercase tracking-[1.5px] text-accent font-bold">
        From Arcanum
      </span>
      <p className="text-xs font-body text-text">
        You've used your 3 free questions on this deal. Unlock Arcanum to
        continue asking questions and iterate on this deal.
      </p>
      {suggestionChips != null && suggestionChips.chips.length > 0 && (
        <div
          data-testid="in-thread-gate-teasers"
          className="flex flex-col gap-1.5"
        >
          <span className="text-[9px] font-mono uppercase tracking-[1px] text-text-muted">
            What you could ask next
          </span>
          <div className="flex flex-wrap items-center gap-2">
            {suggestionChips.chips.map((chip) => (
              <span
                key={chip}
                data-testid="in-thread-gate-teaser-chip"
                aria-disabled="true"
                className="px-2.5 py-1 border border-panel-border rounded-[2px] text-[11px] font-mono text-text-dim bg-input-bg select-none"
              >
                {chip}
              </span>
            ))}
          </div>
        </div>
      )}
      <a
        href={authUrlWithShare('/signup', shareId)}
        data-testid="in-thread-unlock-cta"
        onClick={() =>
          posthog.capture('deal_signup_cta_clicked', {
            source: 'gate',
            share_id: shareId,
          })
        }
        className="self-start px-3 py-1.5 border border-accent/40 rounded-[2px] bg-accent/10 text-accent text-xs font-mono font-semibold tracking-[0.3px] uppercase no-underline hover:bg-accent/20 transition-colors"
      >
        Unlock Arcanum →
      </a>
    </div>
  );
}
